// Command Tools - Available actions for the AI to use (Updated with Sketchfab support)
export const COMMAND_TOOLS = [
  {
    name: 'add_object',
    description:
      'Add objects to the 3D scene. Include optional visual properties at create time (color, intensity, particleCount, size, etc.). For quantity > 1 always set layout (line/grid/circle/stack) so objects are spaced — never leave them at the same position. For relations like "cone on top of a cube", add the anchor first, then the subject with relativeTo + placement.',
    parameters: {
      type: 'object',
      properties: {
        objectType: {
          type: 'string',
          description: 'Type of object to add',
          enum: [
            'cube',
            'sphere',
            'cylinder',
            'cone',
            'pyramid',
            'capsule',
            'torus',
            'rectangle',
            'ellipse',
            'triangle',
            'plane',
            'rain',
            'snow',
            'fire',
            'stars',
            'clouds',
            'water',
            'foliage',
            'pointlight',
            'directionallight',
            'spotlight',
            'arealight',
            'ambientlight',
            'text',
            'hotspot',
          ],
        },
        quantity: {
          type: 'integer',
          description: 'Number of objects to add (default: 1)',
          minimum: 1,
          maximum: 100,
        },
        layout: {
          type: 'string',
          enum: ['line', 'grid', 'circle', 'stack'],
          description:
            'How to arrange multiple objects. Use "line" for "in a straight line" / "in a row". REQUIRED whenever quantity > 1 so items are not stacked at the same point.',
        },
        layoutAxis: {
          type: 'string',
          enum: ['x', 'y', 'z'],
          description:
            'Axis for line layout: x = left-right, z = front-back, y = vertical column',
        },
        spacing: {
          type: 'number',
          description: 'Distance between objects in a layout (default ~1.5)',
        },
        layoutCols: {
          type: 'integer',
          description: 'Column count for grid layout',
        },
        relativeTo: {
          type: 'string',
          description:
            'Anchor object type or name to place relative to (e.g. "cube"). Create the anchor in an earlier tool call first.',
        },
        placement: {
          type: 'string',
          enum: [
            'on_top',
            'above',
            'below',
            'under',
            'left',
            'right',
            'front',
            'behind',
            'next_to',
          ],
          description:
            'Where to put this object relative to relativeTo. Example: cone on top of cube → relativeTo "cube", placement "on_top".',
        },
        content: {
          type: 'string',
          description:
            'Text content when objectType is text (e.g. "Welcome")',
        },
        fontSize: {
          type: 'number',
          description: 'Text font size (default ~80; large ~120; small ~40)',
        },
        textType: {
          type: 'string',
          enum: ['2D', '3D'],
          description: 'Text mode: 2D or 3D',
        },
        buttonText: {
          type: 'string',
          description: 'Hotspot button label when objectType is hotspot',
        },
        messageText: {
          type: 'string',
          description: 'Hotspot popup message when objectType is hotspot',
        },
        color: {
          type: 'string',
          description:
            'Color as hex (#RRGGBB) or CSS name (blue, red). Use for shapes, particles, and lights when specified.',
        },
        intensity: {
          type: 'number',
          description:
            'Light intensity (e.g. 0.35 dim, 1 normal, 2.5 bright) or particle intensity',
        },
        size: {
          type: 'number',
          description:
            'Particle size, or uniform scale for shapes when scale is omitted',
        },
        opacity: {
          type: 'number',
          description:
            'Opacity 0–1 for shapes and particles (0 = fully transparent)',
        },
        metalness: {
          type: 'number',
          description: 'Shape metalness 0–1 (1 = chrome/metal)',
        },
        roughness: {
          type: 'number',
          description: 'Shape roughness 0–1 (0 = glossy, 1 = matte)',
        },
        emission: {
          type: 'string',
          description: 'Emissive/glow color hex for shapes',
        },
        emissiveIntensity: {
          type: 'number',
          description: 'Glow strength for shapes (e.g. 1.2)',
        },
        wireframe: {
          type: 'boolean',
          description: 'If true, render shape as wireframe',
        },
        visible: {
          type: 'boolean',
          description: 'Object visibility',
        },
        castShadow: {
          type: 'boolean',
          description: 'Whether the object casts shadows',
        },
        particleCount: {
          type: 'integer',
          description:
            'Number of particles (e.g. 400 sparse, 1000 default, 2500 heavy)',
          minimum: 1,
          maximum: 50000,
        },
        range: {
          type: 'number',
          description: 'Emitter / effect range',
        },
        height: {
          type: 'number',
          description: 'Effect height (e.g. rain fall height)',
        },
        windStrength: {
          type: 'number',
          description: 'Wind strength for rain/snow-like effects',
        },
        turbulence: {
          type: 'number',
          description: 'Turbulence for particle effects',
        },
        life: {
          type: 'number',
          description: 'Particle life in seconds',
        },
        shape: {
          type: 'string',
          description: 'Particle sprite shape (circle, square, etc.)',
        },
        shadow: {
          type: 'string',
          description: 'Light shadow: Soft Shadow, Hard Shadow, or No Shadow',
        },
        angle: {
          type: 'number',
          description: 'Spot light cone angle',
        },
        width: {
          type: 'number',
          description: 'Area light width',
        },
        position: {
          type: 'object',
          description:
            'Absolute position. Prefer relativeTo+placement for relations like on top of.',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        rotation: {
          type: 'object',
          description: 'Rotation in degrees',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        scale: {
          type: 'object',
          description: 'Scale of the object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        properties: {
          type: 'object',
          description:
            'Optional nested bag of the same create fields (color, intensity, particleCount, …)',
        },
      },
      required: ['objectType'],
    },
  },
  {
    name: 'add_sketchfab_model',
    description:
      'Add a 3D model to the scene. Searches the SpatialGrid library first for the best match; if none is found, downloads from Sketchfab. Use for cars, characters, furniture, animals, buildings, etc.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Search query for the 3D model (e.g., "car", "dragon", "house", "tree")',
        },
        position: {
          type: 'object',
          description: 'Position where to place the model',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        scale: {
          type: 'object',
          description: 'Scale of the model',
          properties: {
            x: { type: 'number', default: 1 },
            y: { type: 'number', default: 1 },
            z: { type: 'number', default: 1 },
          },
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'add_3d_model',
    description:
      'Same as add_sketchfab_model: library-first 3D model add with Sketchfab fallback.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Search query for the 3D model',
        },
        position: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        scale: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'add_text',
    description:
      'Add 2D or 3D text to the scene. Always set content to the words the user wants displayed. Support color, fontSize, textType (2D|3D), position.',
    parameters: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description: 'The text string to display',
        },
        color: {
          type: 'string',
          description: 'Text color hex or name',
        },
        fontSize: {
          type: 'number',
          description: 'Font size (default 80)',
        },
        textType: {
          type: 'string',
          enum: ['2D', '3D'],
          description: '2D flat text or 3D extruded text',
        },
        fontFamily: { type: 'string' },
        position: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        rotation: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        scale: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
      },
      required: ['content'],
    },
  },
  {
    name: 'add_hotspot',
    description:
      'Add an interactive hotspot marker with a button label and popup message.',
    parameters: {
      type: 'object',
      properties: {
        buttonText: {
          type: 'string',
          description: 'Short label on the hotspot button',
        },
        messageText: {
          type: 'string',
          description: 'Popup / info message shown when interacting',
        },
        color: {
          type: 'string',
          description: 'Button background color',
        },
        buttonColor: { type: 'string' },
        messageBgColor: { type: 'string' },
        buttonShape: {
          type: 'string',
          enum: ['Circle', 'Rectangle'],
        },
        position: {
          type: 'object',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        name: {
          type: 'string',
          description: 'Display name in the left hierarchy panel',
        },
      },
    },
  },
  {
    name: 'remove_object',
    description: 'Remove objects from the 3D scene',
    parameters: {
      type: 'object',
      properties: {
        objectType: {
          type: 'string',
          description:
            "Type of object to remove, or 'all' to remove everything",
        },
        name: {
          type: 'string',
          description: 'Specific object name to remove',
        },
      },
    },
  },
  {
    name: 'modify_object',
    description:
      'Modify properties of existing objects (position, rotation, scale, color, opacity, metalness, roughness, emission, intensity, particleCount). Prefer creating with properties via add_object when possible; use modify for follow-up changes. Always include the fields to change. Set all=true when the user says "all" / plural ("make all cubes orange").',
    parameters: {
      type: 'object',
      properties: {
        objectType: {
          type: 'string',
          description:
            'Type of object to modify (e.g. cube, sphere, rain, pointlight, car, mesh)',
        },
        name: {
          type: 'string',
          description: 'Name of specific object to modify',
        },
        all: {
          type: 'boolean',
          description:
            'If true, modify every matching object of objectType (e.g. "make all cubes orange"). Default false = only the best match.',
        },
        quantity: {
          type: 'integer',
          description:
            'Optional max number of matches to modify. Use -1 with all=true for every match.',
        },
        color: {
          type: 'string',
          description: 'New color as hex (#RRGGBB) or name',
        },
        intensity: {
          type: 'number',
          description: 'New light or particle intensity',
        },
        particleCount: {
          type: 'integer',
          description: 'New particle count',
        },
        size: {
          type: 'number',
          description: 'New particle size or shape scale',
        },
        opacity: {
          type: 'number',
          description: 'New opacity 0–1',
        },
        metalness: { type: 'number' },
        roughness: { type: 'number' },
        emission: { type: 'string' },
        emissiveIntensity: { type: 'number' },
        wireframe: { type: 'boolean' },
        visible: { type: 'boolean' },
        properties: {
          type: 'object',
          description:
            'Properties to modify. Prefer this nested object over top-level fields.',
          properties: {
            color: { type: 'string', description: 'Color in hex format' },
            intensity: { type: 'number' },
            particleCount: { type: 'integer' },
            size: { type: 'number' },
            opacity: { type: 'number' },
            metalness: { type: 'number' },
            roughness: { type: 'number' },
            emission: { type: 'string' },
            position: {
              type: 'object',
              properties: {
                x: { type: 'number' },
                y: { type: 'number' },
                z: { type: 'number' },
              },
            },
            rotation: {
              type: 'object',
              description: 'Rotation in degrees or radians',
              properties: {
                x: { type: 'number' },
                y: { type: 'number' },
                z: { type: 'number' },
              },
            },
            scale: {
              type: 'object',
              properties: {
                x: { type: 'number' },
                y: { type: 'number' },
                z: { type: 'number' },
              },
            },
          },
        },
        position: {
          type: 'object',
          description: 'Optional top-level position if not using properties',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        rotation: {
          type: 'object',
          description: 'Optional top-level rotation if not using properties',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
        scale: {
          type: 'object',
          description: 'Optional top-level scale if not using properties',
          properties: {
            x: { type: 'number' },
            y: { type: 'number' },
            z: { type: 'number' },
          },
        },
      },
    },
  },
  {
    name: 'show_help',
    description: 'Show help information about available commands',
    parameters: {
      type: 'object',
      properties: {
        category: {
          type: 'string',
          description: 'Category of help to show',
          enum: [
            'general',
            'shapes',
            'effects',
            'lights',
            'sketchfab',
            'text',
            'hotspot',
            'commands',
          ],
        },
      },
    },
  },
];

// Tool execution functions
export const executeTool = async (
  toolName,
  parameters,
  scene,
  transformControls,
  store
) => {
  const params = parameters && typeof parameters === 'object' ? parameters : {};

  switch (toolName) {
    case 'add_object':
      return await addObject(params, scene, transformControls, store);
    case 'add_sketchfab_model':
    case 'add_3d_model':
      return await addSketchfabModel(params, scene, store);
    case 'add_text':
      return await executeAddText(params, scene, transformControls);
    case 'add_hotspot':
      return await executeAddHotspot(params, scene, transformControls);
    case 'remove_object':
      return await removeObject(params, scene, store);
    case 'modify_object':
      return await modifyObject(params, scene, store);
    case 'show_help':
      return showHelp(params);
    default:
      throw new Error(`Unknown tool: ${toolName}`);
  }
};

// Tool implementations
async function addObject(parameters, scene, transformControls, store) {
  const props = pickCreateProps(parameters);
  const objectType = parameters.objectType;
  const quantity = props.quantity ?? parameters.quantity ?? 1;
  const position = props.position ?? parameters.position;
  const relativeTo =
    parameters.relativeTo ||
    parameters.anchor ||
    props.relativeTo ||
    null;
  const placement = normalizePlacement(
    parameters.placement || parameters.relation || props.placement
  );

  // Multi-object arrangement: never pile quantity>1 at the same origin
  const layoutName =
    normalizeGroupLayout(parameters.layout || props.layout) ||
    (quantity > 1 && !relativeTo ? 'line' : null);
  const layoutAxis = parameters.layoutAxis || props.layoutAxis || 'x';
  const layoutCols = parameters.layoutCols || props.layoutCols;
  let layoutSpacing =
    parameters.spacing != null
      ? Number(parameters.spacing)
      : props.spacing != null
        ? Number(props.spacing)
        : null;

  const createPayload = { ...parameters, ...props };

  let positions = null;
  if (layoutName && quantity > 1 && !relativeTo) {
    positions = computeGroupPositions(
      quantity,
      {
        layout: layoutName,
        axis: layoutAxis,
        spacing: layoutSpacing ?? 1.5,
        cols: layoutCols,
      },
      position || { x: 0, y: 0, z: 0 }
    );
  }

  const created = [];
  for (let i = 0; i < quantity; i++) {
    const itemPosition = positions
      ? positions[i]
      : relativeTo && placement
        ? null
        : position;

    const obj = await addSingleObject(
      objectType,
      itemPosition,
      scene,
      transformControls,
      store,
      createPayload
    );
    if (obj) created.push(obj);

    // Refine spacing from the first real object so sizes don't overlap
    if (
      i === 0 &&
      obj &&
      positions &&
      quantity > 1 &&
      layoutSpacing == null
    ) {
      layoutSpacing = spacingFromObject(obj, layoutName, 1.5, layoutAxis);
      positions = computeGroupPositions(
        quantity,
        {
          layout: layoutName,
          axis: layoutAxis,
          spacing: layoutSpacing,
          cols: layoutCols,
        },
        position || { x: 0, y: 0, z: 0 }
      );
      if (obj.position && positions[0]) {
        obj.position.set(
          positions[0].x ?? 0,
          positions[0].y ?? 0,
          positions[0].z ?? 0
        );
        obj.updateMatrixWorld?.(true);
      }
    }

    if (obj && relativeTo && placement && scene) {
      const anchor = findAnchorObject(scene, relativeTo);
      if (anchor && anchor !== obj) {
        applyRelativePlacement(obj, anchor, placement);
      } else {
        console.warn(
          `[add_object] relativeTo "${relativeTo}" not found for ${objectType}`
        );
      }
    }

    if (quantity > 1) {
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
  }

  const placed =
    relativeTo && placement
      ? ` (${String(placement).replace(/_/g, ' ')} of ${relativeTo})`
      : layoutName && quantity > 1
        ? ` in a ${layoutName}`
        : '';

  return {
    success: true,
    message: `Added ${quantity} ${objectType}${quantity > 1 ? 's' : ''}${placed} to scene`,
    createdCount: created.length,
  };
}

async function addSketchfabModel(parameters, scene, store) {
  const { query, position, scale } = parameters;

  try {
    const { addModelFromLibraryOrSketchfab } = await import(
      './modelAddService'
    );

    const options = {};
    if (position) options.position = position;
    if (scale) options.scale = scale;

    const result = await addModelFromLibraryOrSketchfab(
      query,
      scene,
      store,
      options
    );

    if (result.success) {
      return {
        success: true,
        message: result.message,
        modelInfo: result.modelInfo,
        source: result.source,
      };
    } else {
      return {
        success: false,
        message: result.message || `Failed to add 3D model for "${query}"`,
        error: result.error,
      };
    }
  } catch (error) {
    console.error('3D model addition error:', error);
    return {
      success: false,
      message: `Failed to add 3D model: ${error.message}`,
      error: error.message,
    };
  }
}

async function removeObject(parameters, scene, store) {
  let { objectType, quantity, name } = parameters;
  

  if (objectType === 'all' || objectType === 'everything') {
    return await removeAllObjects(scene, store);
  }

  scene.traverse(async(obj) => {
    if(!obj.file_name)return;
    if (obj.file_name.toLowerCase().includes(name?.toLowerCase()) || obj.file_name.toLowerCase().includes(objectType?.toLowerCase())) {
      if (quantity>0 || quantity === -1) {
        
        await scene.remove(obj);
        if (quantity !== -1) quantity--;
        if (quantity === 0) return;
      }
      return;
    }
  });

  return await removeObjectsByType(objectType, name, scene, store, quantity);
}

async function modifyObject(parameters = {}, scene, store) {
  try {
    const raw = parameters && typeof parameters === 'object' ? parameters : {};
    const objectType = String(
      raw.objectType || raw.type || raw.target || ''
    ).trim();
    const name = String(
      raw.name || raw.objectName || raw.query || ''
    ).trim();
    const modifyAll =
      raw.all === true ||
      raw.all === 'true' ||
      raw.targetAll === true ||
      String(raw.scope || '').toLowerCase() === 'all' ||
      Number(raw.quantity) === -1;
    const quantityLimit =
      Number.isFinite(Number(raw.quantity)) && Number(raw.quantity) > 0
        ? Math.floor(Number(raw.quantity))
        : null;

    const picked = pickCreateProps(raw);
    const nested =
      raw.properties &&
      typeof raw.properties === 'object' &&
      !Array.isArray(raw.properties)
        ? raw.properties
        : {};

    // Merge nested + top-level transform fields the model may send
    const properties = { ...nested, ...picked };
    for (const key of ['position', 'rotation', 'scale', 'color']) {
      if (raw[key] != null && properties[key] == null) {
        properties[key] = raw[key];
      }
    }
    if (properties.color != null) {
      properties.color = normalizeColor(properties.color) || properties.color;
    }

    // Flat fields: position_x / x / posX, etc.
    const readAxis = (kind, axis) => {
      const candidates = [
        raw[`${kind}_${axis}`],
        raw[`${kind}${axis.toUpperCase()}`],
        nested[`${kind}_${axis}`],
        nested[`${kind}${axis.toUpperCase()}`],
        // bare x/y/z only for position when nested under properties
        kind === 'position' ? nested[axis] ?? raw[axis] : undefined,
      ];
      return candidates.find((v) => v !== undefined && v !== null);
    };

    for (const kind of ['position', 'rotation', 'scale']) {
      const x = readAxis(kind, 'x');
      const y = readAxis(kind, 'y');
      const z = readAxis(kind, 'z');
      if (x === undefined && y === undefined && z === undefined) continue;

      const existing =
        properties[kind] &&
        typeof properties[kind] === 'object' &&
        !Array.isArray(properties[kind])
          ? properties[kind]
          : {};
      properties[kind] = {
        ...existing,
        ...(x !== undefined ? { x } : {}),
        ...(y !== undefined ? { y } : {}),
        ...(z !== undefined ? { z } : {}),
      };
    }

    // Semantic position strings: "left", "extreme left", "right", etc.
    properties.position = resolveSemanticPosition(
      properties.position,
      raw.direction || raw.side || nested.direction
    );

    if (!objectType && !name) {
      return {
        success: false,
        message:
          'modify_object requires at least objectType or name to find the object',
      };
    }

    const hasTransform =
      hasVec3(properties.position) ||
      hasVec3(properties.rotation) ||
      hasVec3(properties.scale) ||
      typeof properties.scale === 'number' ||
      properties.color != null ||
      properties.opacity != null ||
      properties.metalness != null ||
      properties.roughness != null ||
      properties.emission != null ||
      properties.emissive != null ||
      properties.emissiveIntensity != null ||
      properties.wireframe != null ||
      properties.visible != null ||
      properties.castShadow != null ||
      properties.receiveShadow != null ||
      properties.transparency != null ||
      properties.size != null ||
      properties.intensity != null ||
      properties.particleCount != null ||
      properties.range != null ||
      properties.height != null ||
      properties.content != null ||
      properties.fontSize != null ||
      properties.buttonText != null ||
      properties.messageText != null ||
      raw.content != null ||
      raw.fontSize != null ||
      raw.buttonText != null ||
      raw.messageText != null;

    if (!hasTransform) {
      return {
        success: false,
        message:
          'modify_object requires position, rotation, scale, color, and/or other visual properties to change',
      };
    }

    const typeLower = objectType.toLowerCase();

    const multiOpts = { all: modifyAll, quantity: quantityLimit };

    // Text: rebuild via handleAddText with updated content/color/size
    if (typeLower === 'text' || isTextNameHint(name, scene)) {
      const textResult = await modifyTextInScene(
        typeLower,
        name,
        { ...raw, ...properties },
        scene,
        store,
        multiOpts
      );
      if (textResult) return textResult;
    }

    // Hotspot: update button/message via handleUpdateHotSpot
    if (typeLower === 'hotspot' || isHotspotNameHint(name, scene)) {
      const hotspotResult = modifyHotspotInScene(
        typeLower,
        name,
        { ...raw, ...properties },
        scene,
        multiOpts
      );
      if (hotspotResult) return hotspotResult;
    }

    // Particles: update Redux so useParticleSync runs dedicated updaters
    if (isParticleType(typeLower) || isParticleNameHint(name, store)) {
      const particleResult = modifyParticleViaRedux(
        typeLower,
        name,
        properties,
        store,
        multiOpts
      );
      if (particleResult) return particleResult;
    }

    // Lights: update light_data so useLightHook syncs Three.js lights
    if (isLightType(typeLower) || isLightNameHint(name, store)) {
      const lightResult = modifyLightViaRedux(
        typeLower,
        name,
        properties,
        store,
        multiOpts
      );
      if (lightResult) return lightResult;
    }

    const objects = findObjectsInScene(objectType, name, scene, store, multiOpts);
    if (!objects.length) {
      return {
        success: false,
        message: `Object not found: ${objectType || ''} ${name || ''}`.trim(),
      };
    }

    for (const object of objects) {
      applyVec3ToObject(object, 'position', properties.position);
      applyVec3ToObject(object, 'rotation', properties.rotation, {
        degreesToRadians: true,
      });
      applyScaleToObject(object, properties.scale ?? properties.uniformScale);
      if (properties.scale == null && properties.size != null) {
        const s = Number(properties.size);
        if (Number.isFinite(s) && s > 0) {
          applyScaleToObject(object, s);
        }
      }
      applyMeshVisualProps(object, properties);

      object.updateMatrixWorld?.(true);
    }

    const label = objectType || objects[0].name || objects[0].file_name || 'object';
    return {
      success: true,
      message:
        objects.length > 1
          ? `Modified ${objects.length} ${label}${/s$/i.test(label) ? '' : 's'}`
          : `Modified ${label} ${name || ''}`.trim(),
    };
  } catch (err) {
    console.error('modify_object failed:', err, parameters);
    return {
      success: false,
      message: `modify_object failed: ${err?.message || String(err)}`,
    };
  }
}

function isTextNameHint(name, scene) {
  if (!name || !scene) return false;
  const n = name.toLowerCase();
  let found = false;
  scene.traverse((obj) => {
    if (found || obj.deleteType !== 'text') return;
    const fn = (obj.file_name || obj.name || '').toLowerCase();
    if (fn === n || fn.includes(n) || obj.content?.toLowerCase?.().includes(n)) {
      found = true;
    }
  });
  return found;
}

function isHotspotNameHint(name, scene) {
  if (!name || !scene) return false;
  const n = name.toLowerCase();
  let found = false;
  scene.traverse((obj) => {
    if (found || obj.deleteType !== 'hotspot') return;
    const fn = (obj.file_name || obj.name || '').toLowerCase();
    const btn = (
      obj.userData?.buttonText ||
      obj.buttonText ||
      ''
    ).toLowerCase();
    if (fn === n || fn.includes(n) || btn.includes(n)) found = true;
  });
  return found;
}

async function modifyTextInScene(typeLower, name, properties, scene, store) {
  if (!scene) return null;

  let match = null;
  const n = (name || '').toLowerCase();
  scene.traverse((obj) => {
    if (obj.deleteType !== 'text') return;
    if (!n) {
      match = obj; // last text if no name
      return;
    }
    const fn = (obj.file_name || '').toLowerCase();
    const content = (obj.content || obj.userData?.text_material?.content || '')
      .toLowerCase();
    if (
      obj.name === name ||
      fn === n ||
      fn.includes(n) ||
      content.includes(n)
    ) {
      match = obj;
    }
  });

  if (!match && typeLower === 'text') {
    scene.traverse((obj) => {
      if (obj.deleteType === 'text') match = obj;
    });
  }
  if (!match) return null;

  const existing =
    match.userData?.text_material || TextInitialDataFallback();
  const nextContent =
    properties.content != null
      ? String(properties.content)
      : existing.content;
  const nextColor =
    properties.color != null ? properties.color : existing.color;
  const nextFontSize =
    properties.fontSize != null
      ? Number(properties.fontSize)
      : existing.fontSize;

  const context = {
    scene,
    selectedObject: match,
    ...(window.gridPageSceneObjects || {}),
  };

  await handleAddText(
    context,
    {
      ...existing,
      id: match.name || match._id,
      content: nextContent,
      color: nextColor,
      fontSize: nextFontSize,
      textType: properties.textType || existing.textType || '3D',
      position: properties.position || {
        x: match.position.x,
        y: match.position.y,
        z: match.position.z,
      },
    },
    'add'
  );

  return {
    success: true,
    message: `Modified text "${nextContent}"`,
  };
}

function modifyHotspotInScene(typeLower, name, properties, scene) {
  if (!scene) return null;

  let match = null;
  const n = (name || '').toLowerCase();
  scene.traverse((obj) => {
    if (obj.deleteType !== 'hotspot') return;
    if (!n) {
      match = obj;
      return;
    }
    const fn = (obj.file_name || obj.name || '').toLowerCase();
    const btn = String(
      obj.userData?.buttonText || obj.buttonText || ''
    ).toLowerCase();
    if (fn === n || fn.includes(n) || btn.includes(n) || obj.name === name) {
      match = obj;
    }
  });

  if (!match && typeLower === 'hotspot') {
    scene.traverse((obj) => {
      if (obj.deleteType === 'hotspot') match = obj;
    });
  }
  if (!match) return null;

  const existing = match.userData || {};
  const updateData = {
    buttonText:
      properties.buttonText ??
      properties.content ??
      existing.buttonText ??
      '1',
    messageText:
      properties.messageText ??
      properties.message ??
      existing.messageText ??
      'Welcome to spatialgrid',
    buttonColor:
      normalizeColor(
        properties.buttonColor ?? properties.color ?? existing.buttonColor
      ) || existing.buttonColor,
    buttonTextColor:
      normalizeColor(properties.buttonTextColor) || existing.buttonTextColor,
    messageTextColor:
      normalizeColor(properties.messageTextColor) ||
      existing.messageTextColor,
    messageBgColor:
      normalizeColor(
        properties.messageBgColor ??
          properties.backgroundColor ??
          existing.messageBgColor
      ) || existing.messageBgColor,
    buttonShape:
      properties.buttonShape === 'Rectangle'
        ? 'Rectangle'
        : existing.buttonShape || 'Circle',
    buttonOpacity:
      properties.buttonOpacity != null
        ? Number(properties.buttonOpacity)
        : existing.buttonOpacity,
    messageOpacity:
      properties.messageOpacity != null
        ? Number(properties.messageOpacity)
        : existing.messageOpacity,
  };

  if (hasVec3(properties.position)) {
    applyVec3ToObject(match, 'position', properties.position);
  }

  handleUpdateHotSpot(match, updateData, {
    camera: window.gridPageSceneObjects?.currentCamera,
    renderer: window.gridPageSceneObjects?.renderer,
  });

  return {
    success: true,
    message: `Modified hotspot "${updateData.buttonText}"`,
  };
}

function TextInitialDataFallback() {
  return {
    content: 'Spatial Grid',
    color: '#ff6600',
    fontSize: 80,
    textType: '3D',
  };
}

function isParticleNameHint(name, store) {
  if (!name) return false;
  const particles = store.getState().particles?.particles || [];
  const n = name.toLowerCase();
  return particles.some(
    (p) =>
      p.id === name ||
      p.name === name ||
      (p.type && p.type.toLowerCase() === n) ||
      (p.name && p.name.toLowerCase().includes(n))
  );
}

function isLightNameHint(name, store) {
  if (!name) return false;
  const lights = store.getState().assets_object?.light_data || [];
  const n = name.toLowerCase();
  return lights.some(
    (l) =>
      l.id === name ||
      (l.name && l.name.toLowerCase().includes(n)) ||
      (l.light_type && l.light_type.toLowerCase().includes(n))
  );
}

function modifyParticleViaRedux(
  typeLower,
  name,
  properties,
  store,
  { all = false, quantity = null } = {}
) {
  const particles = store.getState().particles?.particles || [];
  if (!particles.length) return null;

  let targets = [];
  if (name) {
    const n = name.toLowerCase();
    targets = particles.filter(
      (p) =>
        p.id === name ||
        p.name === name ||
        (p.name && p.name.toLowerCase().includes(n)) ||
        (p.type && p.type.toLowerCase() === n)
    );
  }
  if (!targets.length && typeLower) {
    targets = particles.filter((p) => p.type === typeLower);
  }
  if (!targets.length) return null;

  if (!all) {
    targets = [targets[targets.length - 1]];
  } else if (quantity != null) {
    targets = targets.slice(0, quantity);
  }

  const updates = {};
  for (const key of [
    'color',
    'intensity',
    'particleCount',
    'size',
    'opacity',
    'range',
    'height',
    'windStrength',
    'turbulence',
    'life',
    'shape',
    'position',
    'rotation',
    'scale',
  ]) {
    if (properties[key] !== undefined && properties[key] !== null) {
      updates[key] = properties[key];
    }
  }
  if (!Object.keys(updates).length) {
    return {
      success: false,
      message: 'No particle properties to update',
    };
  }

  targets.forEach((match) => {
    store.dispatch(updateParticle({ id: match.id, updates }));
  });
  return {
    success: true,
    message:
      targets.length > 1
        ? `Modified ${targets.length} ${typeLower || 'particle'} effect(s)`
        : `Modified particle ${targets[0].name || targets[0].type || targets[0].id}`,
  };
}

function modifyLightViaRedux(
  typeLower,
  name,
  properties,
  store,
  { all = false, quantity = null } = {}
) {
  const lights = store.getState().assets_object?.light_data || [];
  if (!lights.length) return null;

  const typeMap = {
    pointlight: 'Point Light',
    directionallight: 'Directional Light',
    spotlight: 'Spot Light',
    arealight: 'Area Light',
    ambientlight: 'Ambient Light',
    light: null,
  };

  let targets = [];
  if (name) {
    const n = name.toLowerCase();
    targets = lights.filter(
      (l) =>
        l.id === name ||
        (l.name && l.name.toLowerCase().includes(n)) ||
        (l.light_type && l.light_type.toLowerCase().includes(n))
    );
  }
  if (!targets.length && typeLower && typeLower !== 'light') {
    const want = typeMap[typeLower];
    targets = lights.filter((l) => l.light_type === want);
  }
  if (!targets.length && typeLower === 'light') {
    targets = [...lights];
  }
  if (!targets.length) return null;

  if (!all) {
    targets = [targets[targets.length - 1]];
  } else if (quantity != null) {
    targets = targets.slice(0, quantity);
  }

  let updated = 0;
  for (const match of targets) {
    const lightUpdate = { id: match.id };
    if (properties.color != null) lightUpdate.color = properties.color;
    if (properties.intensity != null)
      lightUpdate.intensity = properties.intensity;
    if (properties.shadow != null) lightUpdate.shadow = properties.shadow;
    if (properties.range != null) lightUpdate.range = properties.range;
    if (properties.angle != null) lightUpdate.angle = properties.angle;
    if (properties.width != null) lightUpdate.width = properties.width;
    if (properties.height != null) lightUpdate.height = properties.height;
    if (hasVec3(properties.position)) {
      lightUpdate.position = {
        ...(match.position || {}),
        ...properties.position,
      };
    }
    if (hasVec3(properties.rotation)) {
      lightUpdate.rotation = {
        ...(match.rotation || {}),
        ...properties.rotation,
      };
    }

    if (Object.keys(lightUpdate).length <= 1) continue;

    store.dispatch(
      lightDataUpdate({
        actionType: 'update',
        light: lightUpdate,
      })
    );
    updated += 1;
  }

  if (!updated) {
    return {
      success: false,
      message: 'No light properties to update',
    };
  }

  return {
    success: true,
    message:
      updated > 1
        ? `Modified ${updated} lights`
        : `Modified light ${targets[0].name || targets[0].light_type || targets[0].id}`,
  };
}

function hasVec3(value) {
  if (value == null) return false;
  if (typeof value === 'number') return true;
  if (typeof value !== 'object' || Array.isArray(value)) return false;
  return ['x', 'y', 'z'].some((k) => value[k] !== undefined && value[k] !== null);
}

/** Map phrases like "extreme left" into numeric xyz. */
function resolveSemanticPosition(position, direction) {
  const hint = String(
    direction ||
      (typeof position === 'string' ? position : '') ||
      position?.direction ||
      ''
  )
    .toLowerCase()
    .trim();

  const base =
    position && typeof position === 'object' && !Array.isArray(position)
      ? { ...position }
      : {};

  if (!hint) return Object.keys(base).length ? base : position;

  const EXTREME = 8;
  const NORMAL = 3;
  const isExtreme = /extreme|far|very/.test(hint);

  if (/left/.test(hint)) base.x = -(isExtreme ? EXTREME : NORMAL);
  if (/right/.test(hint)) base.x = isExtreme ? EXTREME : NORMAL;
  if (/forward|front/.test(hint)) base.z = -(isExtreme ? EXTREME : NORMAL);
  if (/back|behind/.test(hint)) base.z = isExtreme ? EXTREME : NORMAL;
  if (/up|above/.test(hint)) base.y = isExtreme ? EXTREME : NORMAL;
  if (/down|below/.test(hint)) base.y = -(isExtreme ? EXTREME : NORMAL);
  if (/origin|center|middle/.test(hint)) {
    base.x = 0;
    base.y = base.y ?? 0;
    base.z = 0;
  }

  return Object.keys(base).length ? base : position;
}

function applyVec3ToObject(object, propName, value, { degreesToRadians } = {}) {
  if (!object || value == null) return;
  const target = object[propName];
  if (!target) return;

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  let x;
  let y;
  let z;
  if (typeof value === 'number') {
    x = y = z = value;
  } else if (typeof value === 'object') {
    x = toNum(value.x);
    y = toNum(value.y);
    z = toNum(value.z);
  } else {
    return;
  }

  if (degreesToRadians) {
    const convert = (n) => {
      if (n === undefined) return undefined;
      return Math.abs(n) > Math.PI * 2 + 0.01 ? (n * Math.PI) / 180 : n;
    };
    x = convert(x);
    y = convert(y);
    z = convert(z);
  }

  const nextX = x !== undefined ? x : target.x;
  const nextY = y !== undefined ? y : target.y;
  const nextZ = z !== undefined ? z : target.z;

  if (typeof target.set === 'function') {
    target.set(nextX, nextY, nextZ);
  } else {
    target.x = nextX;
    target.y = nextY;
    target.z = nextZ;
  }
}

function applyScaleToObject(object, scale) {
  if (!object || scale == null || !object.scale) return;
  if (typeof scale === 'number') {
    object.scale.set(scale, scale, scale);
    return;
  }
  if (typeof scale !== 'object') return;
  const uniform = Number(scale.uniform ?? scale.uniformScale);
  const sx = Number(scale.x);
  const sy = Number(scale.y);
  const sz = Number(scale.z);
  object.scale.set(
    Number.isFinite(sx) ? sx : Number.isFinite(uniform) ? uniform : object.scale.x,
    Number.isFinite(sy) ? sy : Number.isFinite(uniform) ? uniform : object.scale.y,
    Number.isFinite(sz) ? sz : Number.isFinite(uniform) ? uniform : object.scale.z
  );
}

function showHelp(parameters) {
  const { category = 'general' } = parameters;
  const guides = {
    general:
      'Try: add cube | add blue rain | add text Hello | add hotspot Info | add car model | remove all cubes | help text',
    shapes:
      'Shapes: cube, sphere, cylinder, cone, pyramid, capsule, torus, rectangle, ellipse, triangle, plane. Example: add 3 red cubes',
    effects:
      'Effects: rain, snow, fire, stars, clouds, water, foliage. Example: add heavy blue snow',
    lights:
      'Lights: pointlight, directionallight, spotlight, arealight, ambientlight. Example: add a dim red spotlight',
    text:
      'Text: add text Hello World | add blue text Welcome | add large 2d text Sale | change text content via modify',
    hotspot:
      'Hotspot: add hotspot Info | add hotspot labelled Start with message Begin here | add red hotspot Next',
    models:
      '3D models: add a car model | download a dragon — searches library first, then Sketchfab',
  };
  const message = guides[category] || guides.general;
  return { success: true, message };
}

// Helper functions
async function addSingleObject(
  objectType,
  position,
  scene,
  transformControls,
  store,
  createProps = {}
) {
  const normalizedType = objectType.toLowerCase();
  const props = createProps && typeof createProps === 'object' ? createProps : {};

  // Handle shapes
  if (
    [
      'cube',
      'sphere',
      'cylinder',
      'cone',
      'pyramid',
      'capsule',
      'torus',
      'rectangle',
      'ellipse',
      'triangle',
      'plane',
    ].includes(normalizedType)
  ) {
    const shapeName =
      normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);
    const mesh = await HandleShapeClick({
      action: shapeName,
      scene,
      transformControls,
    });

    if (position && mesh?.position) {
      mesh.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    }
    if (mesh) {
      applyVec3ToObject(mesh, 'rotation', props.rotation, {
        degreesToRadians: true,
      });
      applyScaleToObject(mesh, props.scale);
      // size on shapes → uniform scale when scale omitted
      if (props.scale == null && props.size != null) {
        const s = Number(props.size);
        if (Number.isFinite(s) && s > 0) {
          applyScaleToObject(mesh, s);
        }
      }
      applyMeshVisualProps(mesh, props);
    }

    return mesh;
  }

  // Handle particle effects (terrain intentionally omitted)
  if (
    [
      'rain',
      'snow',
      'fire',
      'stars',
      'clouds',
      'water',
      'foliage',
    ].includes(normalizedType)
  ) {
    const effectMap = {
      water: handleWater,
      clouds: handleClouds,
      stars: handleStars,
      rain: handleRain,
      snow: handleSnow,
      fire: handleFire,
      foliage: handleFoliage,
    };

    const fn = effectMap[normalizedType];
    if (fn) {
      const particleId = `${normalizedType}_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const particleData = {
        id: particleId,
        type: normalizedType,
        name: normalizedType,
      };
      // Forward only known visual props — omit undefined so handler defaults apply
      for (const key of [
        'color',
        'particleCount',
        'size',
        'opacity',
        'range',
        'height',
        'intensity',
        'windStrength',
        'turbulence',
        'life',
        'shape',
        'position',
        'rotation',
        'scale',
      ]) {
        if (props[key] !== undefined && props[key] !== null) {
          particleData[key] = props[key];
        }
      }
      fn(scene, particleData);
      // Redux must match scene props (addParticle merges particleTemplate)
      store.dispatch(
        addParticle({
          ...particleData,
          name: particleId,
          type: normalizedType,
          particleCount: (normalizedType === 'clouds' || normalizedType === 'cloud') ? 20 : 1000,
          isDuplicate: true,
        })
      );
      return { type: 'effect', name: normalizedType, id: particleId };
    }
    return { type: 'effect', name: normalizedType };
  }

  // Handle lights
  if (
    [
      'light',
      'pointlight',
      'directionallight',
      'spotlight',
      'arealight',
      'ambientlight',
    ].includes(normalizedType)
  ) {
    const lightType = getLightTypeFromCommand(normalizedType);
    return await addLightToScene(lightType, position, store, props);
  }

  // Text / hotspot (also available via dedicated add_text / add_hotspot tools)
  if (normalizedType === 'text') {
    return await executeAddText(
      { ...props, position: position || props.position },
      scene,
      transformControls
    );
  }
  if (normalizedType === 'hotspot') {
    return await executeAddHotspot(
      { ...props, position: position || props.position },
      scene,
      transformControls
    );
  }

  throw new Error(`Unknown object type: ${objectType}`);
}

async function removeObjectsByType(objectType, name, scene, store, quantity) {
  const normalizedType = objectType.toLowerCase();

  // Handle Sketchfab models removal
  if (
    normalizedType === 'sketchfab' ||
    normalizedType === 'model' ||
    normalizedType === '3d'
  ) {
    const assetsState = store.getState().assets_object || {};
    const { sceneMeshes = [] } = assetsState;

    const sketchfabModels = sceneMeshes.filter(
      (obj) =>
        obj.userData?.type === 'sketchfab' ||
        obj.userData?.originalType === 'sketchfab_model' ||
        obj.userData?.glb_type === 'sketchfab'
    );

    let removed = 0;
    sketchfabModels.forEach((obj) => {
      if (quantity !== undefined && removed >= quantity) return;
      if (name && obj.name !== name) return;

      scene.remove(obj);
      store.dispatch(removeIntersectedObject(obj));
      store.dispatch(removeSceneObject(obj));

      // Track for deletion
      const payload = {
        type: 'add',
        data: {
          url: obj?.userData?.downloadUrl || obj?.url || obj?.name,
          id: obj?._id || obj?.name,
          deleteType: 'mesh',
          glb_type: 'sketchfab',
          mesh_code: obj?.userData?.mesh_code,
        },
      };
      store.dispatch(set_delete_objects(payload));
      removed++;
    });

    return {
      success: true,
      message: `Removed ${removed} Sketchfab model(s)`,
    };
  }

  if (
    [
      'rain',
      'snow',
      'fire',
      'stars',
      'clouds',
      'water',
      'terrain',
      'foliage',
    ].includes(normalizedType)
  ) {
    // Use Redux particle state as source of truth
    const particlesState = store.getState().particles?.particles || [];
    const candidates = particlesState.filter(
      (p) =>
        p.type === normalizedType && (!name || p.id === name || p.name === name)
    );
    let removed = 0;
    candidates.forEach((p) => {
      const obj = scene.getObjectByName(p.id) || scene.getObjectByName(p.name);
      if (obj) {
        scene.remove(obj);
        removed += 1;
      }
      store.dispatch(removeParticle(p.id));
    });
    return {
      success: true,
      message: `Removed ${removed} ${normalizedType} effect(s)`,
    };
  }

  // Handle lights removal
  if (
    normalizedType === 'light' ||
    [
      'pointlight',
      'directionallight',
      'spotlight',
      'arealight',
      'ambientlight',
    ].includes(normalizedType)
  ) {
    const lightData = store.getState().assets_object?.light_data || [];
    const toRemoveLights = lightData.filter((l) => {
      if (normalizedType === 'light') return true;
      const typeMap = {
        pointlight: 'Point Light',
        directionallight: 'Directional Light',
        spotlight: 'Spot Light',
        arealight: 'Area Light',
        ambientlight: 'Ambient Light',
      };
      return l.light_type === typeMap[normalizedType];
    });

    toRemoveLights.forEach((l) => {
      store.dispatch(lightDataUpdate({ actionType: 'remove', id: l.id }));
    });

    return {
      success: true,
      message: `Removed ${toRemoveLights.length} light(s)`,
    };
  }

  // Remove shapes and other scene objects using Redux scene arrays
  const assetsState = store.getState().assets_object || {};
  const {
    sceneMeshes = [],
    sceneImages = [],
    sceneVideos = [],
    sceneAudios = [],
    sceneText = [],
    sceneElements = [],
    sceneHotspots = [],
  } = assetsState;

  const shapeNames = [
    'cube',
    'sphere',
    'cylinder',
    'cone',
    'pyramid',
    'capsule',
    'torus',
    'rectangle',
    'ellipse',
    'triangle',
    'plane',
  ];
  const mediaMap = {
    image: ['image', 'img', 'picture', 'photo'],
    video: ['video'],
    audio: ['audio', 'sound'],
  };
  const meshKeywords = ['mesh', 'model', 'object', 'glb', '3d'];

  const allCandidates = [
    ...sceneMeshes,
    ...sceneImages,
    ...sceneVideos,
    ...sceneAudios,
    ...sceneText,
    ...sceneElements,
    ...sceneHotspots,
  ];
  const toRemove = allCandidates.filter((obj) => {
    if (name && obj.name !== name) return false;
    const ud = obj.userData || {};
    const originalType = (ud.originalType || '').toLowerCase();
    const userMeshCode = (ud.mesh_code || '').toLowerCase();
    const meshCode = (obj.mesh_code || '').toLowerCase();
    const objName = (obj.name || '').toLowerCase();
    const deleteType = (obj.deleteType || '').toLowerCase();

    // Text / hotspot
    if (normalizedType === 'text') return deleteType === 'text';
    if (normalizedType === 'hotspot') return deleteType === 'hotspot';

    // Shapes
    if (shapeNames.includes(normalizedType) && ud.type === 'shape') {
      return (
        originalType === normalizedType ||
        userMeshCode === normalizedType ||
        meshCode === normalizedType
      );
    }

    // Meshes (including Sketchfab models)
    if (meshKeywords.includes(normalizedType)) {
      return obj.deleteType === 'mesh' || obj.objType === '3d';
    }

    // Media
    if (mediaMap.image.includes(normalizedType)) return obj.objType === 'image';
    if (mediaMap.video.includes(normalizedType)) return obj.objType === 'video';
    if (mediaMap.audio.includes(normalizedType)) return obj.objType === 'audio';

    // Fallback textual match
    return (
      objName.includes(normalizedType) ||
      userMeshCode.includes(normalizedType) ||
      meshCode.includes(normalizedType) ||
      deleteType === normalizedType
    );
  });
  toRemove.forEach((obj) => {
    if (quantity == 0) return;
    quantity--;

    // Dispose of geometries, materials, and textures before removing
    const disposeObject = (object) => {
      if (object.geometry) {
        object.geometry.dispose();
      }
      if (object.material) {
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => {
          // Dispose all textures
          if (material.map) material.map.dispose();
          if (material.lightMap) material.lightMap.dispose();
          if (material.bumpMap) material.bumpMap.dispose();
          if (material.normalMap) material.normalMap.dispose();
          if (material.specularMap) material.specularMap.dispose();
          if (material.envMap) material.envMap.dispose();
          if (material.alphaMap) material.alphaMap.dispose();
          if (material.aoMap) material.aoMap.dispose();
          if (material.displacementMap) material.displacementMap.dispose();
          if (material.emissiveMap) material.emissiveMap.dispose();
          if (material.gradientMap) material.gradientMap.dispose();
          if (material.metalnessMap) material.metalnessMap.dispose();
          if (material.roughnessMap) material.roughnessMap.dispose();
          material.dispose();
        });
      }
    };

    // Traverse and dispose all children
    if (obj) {
      obj.traverse((child) => {
        disposeObject(child);
      });
      disposeObject(obj);
    }

    if (scene && obj) {
      scene.remove(obj);
    }
    store.dispatch(removeIntersectedObject(obj));
    store.dispatch(removeSceneObject(obj));
    // Track for deletion on publish
    const deleteType = (obj.deleteType || obj.objType || '').toLowerCase();
    if (['mesh', 'image', 'video', 'audio', 'text', 'hotspot'].includes(deleteType)) {
      const payload = {
        type: 'add',
        data: {
          url: obj?.url || obj?._id || obj?.name,
          id: obj?._id,
          deleteType: deleteType,
          glb_type: obj?.glb_type,
          mesh_code: obj?.mesh_code,
        },
      };
      store.dispatch(set_delete_objects(payload));
    }
  });

  // Sync assets_data by pruning removed objects from their respective type arrays
  const removedByType = toRemove.reduce((acc, obj) => {
    const rawType = (obj.deleteType || obj.objType || '').toLowerCase();
    const typeMap = {
      image: 'image',
      img: 'image',
      mesh: 'mesh',
      glb: 'mesh',
      '3d': 'mesh',
      video: 'video',
      audio: 'audio',
      text: 'text',
      hotspot: 'hotspot',
      // ignore particles and shapes for assets_data
    };
    const key = typeMap[rawType];
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(obj);
    return acc;
  }, {});

  Object.entries(removedByType).forEach(([type, removedList]) => {
    const current = store.getState().assets_object?.assets_data?.[type] || [];
    const filtered = current.filter((item) => {
      return !removedList.some((obj) => {
        const sameRef = item === obj;
        const sameId = item?._id && obj?._id && item._id === obj._id;
        const sameUrl = item?.url && obj?.url && item.url === obj.url;
        const sameName = item?.name && obj?.name && item.name === obj.name;
        return sameRef || sameId || sameUrl || sameName;
      });
    });
    store.dispatch(update_assets_data({ type, data: filtered }));
  });

  // If meshes were the target and none are left in scene, hard-clear assets_data.mesh
  if (meshKeywords.includes(normalizedType)) {
    const remainingMeshes = (store.getState().assets_object?.sceneMeshes || [])
      .length;
    if (remainingMeshes === 0) {
      store.dispatch(update_assets_data({ type: 'mesh', data: [] }));
      store.dispatch(setSceneMeshes([]));
    }
  }

  return {
    success: true,
    message: `Removed ${toRemove.length} ${normalizedType} object(s)`,
  };
}

async function removeAllObjects(scene, store) {
  const meshesToRemove = [];
  scene.traverse((obj) => {
    if (obj.userData && obj.userData.type === 'shape') {
      meshesToRemove.push(obj);
    }
  });

  meshesToRemove.forEach((obj) => {
    scene.remove(obj);
    store.dispatch(removeIntersectedObject(obj));
    store.dispatch(removeSceneObject(obj));
  });

  // Remove all particle effects
  [
    'rain',
    'snow',
    'fire',
    'stars',
    'clouds',
    'water',
    'terrain',
    'foliage',
  ].forEach((effect) => {
    const toRemove = [];
    scene.traverse((child) => {
      if (child.deleteType === 'particle' && child.objType === effect) {
        toRemove.push(child);
      }
    });
    toRemove.forEach((obj) => {
      scene.remove(obj);
      if (obj.name) {
        store.dispatch(removeParticle(obj.name));
      }
    });
  });

  store.dispatch(clearAllIntersectedObjects());
  store.dispatch(clearParticles());
  // Also clear assets_data for removable types
  ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].forEach((type) => {
    const hasAny =
      (store.getState().assets_object?.assets_data?.[type] || []).length > 0;
    if (hasAny) {
      store.dispatch(update_assets_data({ type, data: [] }));
    }
  });
  // Also clear scene arrays in Redux to keep state in sync
  const assetsState = store.getState().assets_object || {};
  const {
    sceneMeshes = [],
    sceneImages = [],
    sceneVideos = [],
    sceneAudios = [],
    sceneText = [],
    sceneElements = [],
    sceneHotspots = [],
  } = assetsState;

  const everyObject = [
    ...sceneMeshes,
    ...sceneImages,
    ...sceneVideos,
    ...sceneAudios,
    ...sceneText,
    ...sceneElements,
    ...sceneHotspots,
  ];

  everyObject.forEach((obj) => {
    if (scene && obj) {
      scene.remove(obj);
    }
    store.dispatch(removeSceneObject(obj));
    const deleteType = (obj.deleteType || obj.objType || '').toLowerCase();
    if (['mesh', 'image', 'video', 'audio', 'text', 'hotspot'].includes(deleteType)) {
      const payload = {
        type: 'add',
        data: {
          url: obj?.url || obj?._id || obj?.name,
          id: obj?._id,
          deleteType: deleteType,
          glb_type: obj?.glb_type,
          mesh_code: obj?.mesh_code,
        },
      };
      store.dispatch(set_delete_objects(payload));
    }
  });

  return {
    success: true,
    message: `Removed ${meshesToRemove.length} objects and all effects`,
  };
}

function isSceneDescendant(child, ancestor) {
  if (!child || !ancestor || child === ancestor) return false;
  let p = child.parent;
  while (p) {
    if (p === ancestor) return true;
    p = p.parent;
  }
  return false;
}

/**
 * Score how well a scene object matches objectType / name.
 * Shared by single- and multi-target modify.
 */
function scoreObjectMatch(obj, typeNeedle, nameNeedle) {
  if (!obj || !obj.isObject3D) return -1;

  const objName = (obj.name || '').toString().toLowerCase();
  const fileName = (
    obj.file_name ||
    obj.asset_file_name ||
    obj.userData?.file_name ||
    ''
  )
    .toString()
    .toLowerCase();
  const meshCode = (obj.userData?.mesh_code || obj.mesh_code || '')
    .toString()
    .toLowerCase();
  const originalType = (obj.userData?.originalType || '')
    .toString()
    .toLowerCase();
  const udType = (obj.userData?.type || '').toString().toLowerCase();

  let score = 0;

  if (nameNeedle) {
    if (objName === nameNeedle || fileName === nameNeedle) score += 100;
    else if (objName.includes(nameNeedle) || fileName.includes(nameNeedle))
      score += 50;
    else return -1;
  }

  if (typeNeedle) {
    if (
      originalType === typeNeedle ||
      meshCode === typeNeedle ||
      objName === typeNeedle ||
      fileName === typeNeedle
    ) {
      score += 40;
    } else if (
      originalType.includes(typeNeedle) ||
      meshCode.includes(typeNeedle) ||
      objName.includes(typeNeedle) ||
      fileName.includes(typeNeedle) ||
      udType.includes(typeNeedle)
    ) {
      score += 20;
    } else if (udType === 'shape' && typeNeedle) {
      // shapes often named "Cube" while type is requested as "cube"
      score += 5;
    } else if (!nameNeedle) {
      return -1;
    }
  }

  // Prefer actual scene assets over helpers/cameras
  if (obj.userData?.type === 'shape' || obj.deleteType || obj.objType) {
    score += 10;
  }

  return score;
}

/**
 * Find matching objects. With all=false returns best match only;
 * with all=true returns every strong type/name match (roots only).
 */
function findObjectsInScene(
  objectType,
  name,
  scene,
  store,
  { all = false, quantity = null } = {}
) {
  if (!scene && !store?.getState) return [];

  const typeNeedle = (objectType || '').toString().toLowerCase().trim();
  const nameNeedle = (name || '').toString().toLowerCase().trim();
  // Require a real type/name hit — exclude the weak "+5 any shape" alone
  const minScore = nameNeedle ? 50 : 20;

  const scored = [];
  const seen = new Set();

  const consider = (obj) => {
    if (!obj) return;
    const key = obj.uuid || obj.id || obj.name || obj;
    if (seen.has(key)) return;
    const score = scoreObjectMatch(obj, typeNeedle, nameNeedle);
    if (score < minScore) return;
    seen.add(key);
    scored.push({ obj, score });
  };

  if (scene?.traverse) {
    scene.traverse((obj) => consider(obj));
  }

  if (store?.getState) {
    try {
      const assets = store.getState().assets_object || {};
      const candidates = [
        ...(assets.sceneMeshes || []),
        ...(assets.sceneElements || []),
        ...(assets.sceneImages || []),
        ...(assets.sceneVideos || []),
        ...(assets.sceneAudios || []),
        ...(assets.sceneText || []),
        ...(assets.sceneHotspots || []),
      ];
      candidates.forEach(consider);
    } catch (e) {
      console.warn('findObjectsInScene store fallback failed:', e);
    }
  }

  if (!scored.length) return [];

  scored.sort((a, b) => b.score - a.score);

  if (!all) {
    const limit = quantity != null && quantity > 0 ? quantity : 1;
    return scored.slice(0, limit).map((s) => s.obj);
  }

  // Drop child meshes when a parent already matches (avoid double-coloring)
  const roots = [];
  for (const { obj } of scored) {
    if (roots.some((r) => isSceneDescendant(obj, r))) continue;
    // If this object is ancestor of an already-picked child, replace child
    for (let i = roots.length - 1; i >= 0; i--) {
      if (isSceneDescendant(roots[i], obj)) roots.splice(i, 1);
    }
    roots.push(obj);
  }

  if (quantity != null && quantity > 0) {
    return roots.slice(0, quantity);
  }
  return roots;
}

function findObjectInScene(objectType, name, scene, store) {
  const found = findObjectsInScene(objectType, name, scene, store, {
    all: false,
  });
  return found[0] || null;
}

function getLightTypeFromCommand(command) {
  if (command.includes('point')) return 'Point Light';
  if (command.includes('directional')) return 'Directional Light';
  if (command.includes('spot')) return 'Spot Light';
  if (command.includes('area')) return 'Area Light';
  if (command.includes('ambient')) return 'Ambient Light';
  return 'Point Light';
}

async function addLightToScene(lightType, position, store, createProps = {}) {
  const lightId = `light_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const props = createProps && typeof createProps === 'object' ? createProps : {};

  const newLight = {
    id: lightId,
    name: lightType,
    light_type: lightType,
    color: props.color != null ? props.color : '#ffffff',
    intensity:
      props.intensity != null
        ? props.intensity
        : lightType === 'Area Light'
          ? 10
          : 1,
    position: position || props.position || { x: 0, y: 5, z: 0 },
    rotation: props.rotation || { x: 0, y: 0, z: 0 },
    shadow: props.shadow || 'Soft Shadow',
    range:
      props.range != null
        ? props.range
        : lightType === 'Point Light' || lightType === 'Spot Light'
          ? 100
          : undefined,
    angle:
      props.angle != null
        ? props.angle
        : lightType === 'Spot Light'
          ? Math.PI / 3
          : undefined,
    width:
      props.width != null
        ? props.width
        : lightType === 'Area Light'
          ? 50
          : undefined,
    height:
      props.height != null
        ? props.height
        : lightType === 'Area Light'
          ? 50
          : undefined,
    hall: false,
    helperVisible: false,
    createdAt: new Date().toISOString(),
  };

  store.dispatch(
    lightDataUpdate({
      actionType: 'add',
      light: newLight,
    })
  );

  return { type: 'light', id: lightId, name: lightType };
}

// Import these functions from their respective files
import { HandleShapeClick } from '../../components/GridAssets/AssetsLibrary';
import {
  handleClouds,
  handleFire,
  handleFoliage,
  handleRain,
  handleSnow,
  handleStars,
  handleWater,
} from '../Elements';
import {
  lightDataUpdate,
  removeIntersectedObject,
  removeSceneObject,
  clearAllIntersectedObjects,
  update_assets_data,
  setSceneMeshes,
} from '../../redux/Slice/gridSlice';
import {
  addParticle,
  updateParticle,
  removeParticle,
  clearParticles,
} from '../../redux/Slice/particleSlice';
import { set_delete_objects } from '../../redux/Slice/gridDeleteSlice';
import {
  pickCreateProps,
  normalizeColor,
  applyMeshColor,
  applyMeshOpacity,
  applyMeshVisualProps,
  isParticleType,
  isLightType,
} from './propertyHelpers';
import {
  executeAddText,
  executeAddHotspot,
} from './contentTools';
import { handleAddText } from '../../components/gridpagefunction/Text';
import { handleUpdateHotSpot } from '../../components/gridpagefunction/HotSpot';
import {
  findAnchorObject,
  applyRelativePlacement,
  normalizePlacement,
  normalizeGroupLayout,
  computeGroupPositions,
  spacingFromObject,
} from './spatialLayout';

